import { ref } from "vue";
import StreamStatsService from "@/services/StreamStatsService";
import MetricsService from "@/services/MetricsService";

function composePlatformPushUrl(platform) {
  if (!platform) return;
  let pushUrl = platform.server;
  pushUrl = pushUrl.replace(/\/$/g, "");
  if (pushUrl.indexOf("ustream.tv") > -1 || pushUrl.indexOf("secdn.net") > -1) {
    pushUrl += "/";
  }
  if (!pushUrl.includes("srt://")) {
    pushUrl += "/" + platform.key;
  }
  return pushUrl;
}

export function mediaPulseModel() {
  const mediaPulse = ref(null);
  const mediaPulseTimeoutCtrl = ref(null);
  const scopeAlive = ref(true);
  const activeStream = ref(null);

  const mediaPulsePrimary = ref(null);
  const mediaPulseBackup = ref(null);

  // Increments on every subscribe/unsubscribe so stale loops (whose fetch was
  // in flight during the switch) stop instead of rescheduling themselves.
  let loopToken = 0;

  const subscribeMediaPulse = async (stream, failoverStats = false) => {
    activeStream.value = stream;
    scopeAlive.value = true;
    clearTimeout(mediaPulseTimeoutCtrl.value);
    const token = ++loopToken;
    (async function loop() {
      if (!scopeAlive.value || token !== loopToken) return;

      const currentStream = activeStream.value;
      let timeoutInterval = 5000;
      let shouldUpdatePulse = currentStream?.enabled;
      if (document.hidden) {
        shouldUpdatePulse = false;
        timeoutInterval = 6000;
      }

      if (shouldUpdatePulse) {
        if (!document.hasFocus()) {
          timeoutInterval = 6000;
        }
      }
      if (shouldUpdatePulse) {
        try {
          const streamKey = currentStream?.key;
          const pulseObject = await StreamStatsService.getStreamMediaPulse(
            streamKey
          );
          onMediaPulse(pulseObject);
          if (currentStream.failoverIngest && failoverStats) {
            const backupPulseObject =
              await StreamStatsService.getStreamMediaPulse(
                `${streamKey}_backup`
              );
            mediaPulseBackup.value = backupPulseObject;

            const primaryPulseObject =
              await StreamStatsService.getStreamMediaPulse(
                `${streamKey}_primary`
              );
            mediaPulsePrimary.value = primaryPulseObject;
          }
        } catch (e) {
          console.log(e);
        }
      }
      if (mediaPulse.value?.withoutPushStats && currentStream?.platforms) {
        try {
          const pushIds = [];
          currentStream.platforms.forEach((p) => {
            if (p.metaId && p.enabled) pushIds.push(p.metaId);
          });

          if (pushIds.length) {
            const pushStats = await MetricsService.getPublishPlatformsPulse(
              pushIds,
              currentStream.region._id,
              !!currentStream.pullUrl
            );
            onPublishPlatformsPulse(pushStats);
          }
        } catch (e) {}
      }
      // reschedule, unless unsubscribed/resubscribed while requests were in flight
      if (!scopeAlive.value || token !== loopToken) return;
      mediaPulseTimeoutCtrl.value = setTimeout(loop, timeoutInterval);
    })();
  };

  const onMediaPulse = (pulseObject = {}) => {
    if (mediaPulse.value?.withoutPushStats) {
      const oldPushStats = mediaPulse.value.push_stats;
      pulseObject.push_stats = oldPushStats;
    }
    mediaPulse.value = pulseObject;
  };

  const unsubscribeMediaPulse = () => {
    scopeAlive.value = false;
    loopToken++;
    clearTimeout(mediaPulseTimeoutCtrl.value);
  };

  const onPublishPlatformsPulse = (pushStats = {}) => {
    const push_stats = {};
    activeStream.value?.platforms?.forEach((p) => {
      const mid = p.metaId;
      if (pushStats[mid]) {
        const pushUrl = composePlatformPushUrl(p);
        push_stats[pushUrl] = pushStats[mid].alive ? 1 : 0;
      }
    });

    mediaPulse.value = { ...mediaPulse.value, push_stats };
  };
  return {
    subscribeMediaPulse,
    unsubscribeMediaPulse,
    mediaPulse,
    mediaPulsePrimary,
    mediaPulseBackup,
  };
}
